import Vue from 'vue'

const hasValue = data => Vue.prototype.$isNotEmptyV2(data)

const withTxSk = (route, tx_payment) => {
  if (hasValue(tx_payment.tx_sk)) route.query = { tx_sk: tx_payment.tx_sk }
  return route
}

const readContext = context => ({
  module_page: context.module_page,
  submodule_page: context.submodule_page,
  tx: context.tx_payment || {},
  merchant: context.merchant || {}
})

/**
 * Route to open after a successful card payment, per module.
 * @param {Object} context {module_page, submodule_page, tx_payment, merchant}
 * @returns {Object|null} argument for $router.push, or null when the module has no result page
 */
export function buildSuccessRoute(context) {
  const { module_page, submodule_page, tx, merchant } = readContext(context)

  if (module_page == 'BOOKING_ENGINE') {
    // Additional service pushes params/query only — it stays on the current route by design.
    if (submodule_page === 'BE_ADDITIONAL_SERVICE') {
      return {
        params: { tx_order_id: tx.tx_order_id_2 },
        query: { module: 'additional-service' }
      }
    }
    return withTxSk({ name: 'BePaymentResult', params: { tx_order_id: tx.tx_order_id } }, tx)
  }

  if (module_page == 'CONCIERGE') {
    const route = {
      name: submodule_page === 'CUSTOM_POS' ? 'PosPaymentResult' : 'BeModuleServiceTrxResult',
      params: { merchant_code: merchant.code }
    }
    if (submodule_page === 'CUSTOM_POS') {
      route.params.pos_code = tx.general_data ? tx.general_data.code : ''
      route.params.tx_order_id = tx.tx_order_id_2
    } else {
      route.params.trx_id = tx.tx_order_id_2
    }
    return withTxSk(route, tx)
  }

  if (module_page == 'CONCIERGE_RESERVATION') {
    return {
      name: 'PaymentBillResultHotel',
      params: {
        merchant_code: merchant.code,
        ga_reservation_id: tx.reservation ? tx.reservation.id : '',
        tx_payment_id: tx.tx_order_id
      }
    }
  }

  if (module_page == 'CONCIERGE_TIP') {
    return withTxSk({ name: 'ConciergeTipResult', params: { tx_service_id: tx.tx_service_id } }, tx)
  }

  if (module_page == 'RESTAURANT') {
    if (submodule_page == 'BILL') {
      return {
        name: 'RestaurantPaymentResult',
        params: { merchant_code: merchant.code, tx_payment_id: tx.tx_order_id_2 }
      }
    }
    return {
      name: 'RestaurantTrxResult',
      params: { merchant_code: merchant.code, trx_id: tx.tx_order_id }
    }
  }

  if (module_page == 'TICKET') {
    return {
      name: 'TicketPaymentForm',
      params: { merchant_code: merchant.code, tx_order_id: tx.tx_order_id }
    }
  }

  // UPSELLING has never had a success route here — it keeps rendering in place.
  return null
}

/**
 * Cancel endpoint to call before leaving the payment form, per module.
 * @param {Object} context {module_page, submodule_page, tx_payment, merchant}
 * @returns {Object|null} {api_module, api_url, auth_type} or null when the module cancels without an API call
 */
export function buildCancelEndpoint(context) {
  const { module_page, submodule_page, tx } = readContext(context)

  if (module_page == 'CONCIERGE_RESERVATION') return null

  if (module_page == 'CONCIERGE_TIP') {
    return {
      api_module: 'CONCIERGE',
      api_url: `service/tx-detail/${tx.tx_service_id}/cancel-payment`,
      auth_type: 'concierge_token_direct'
    }
  }

  if (module_page == 'BOOKING_ENGINE') {
    return {
      api_module: 'CORE_HOTEL',
      api_url: submodule_page === 'BE_ADDITIONAL_SERVICE'
        ? `merchant-pos/multiple/transaction/${tx.tx_order_id_2}/cancel-transaction`
        : `tx-detail/${tx.tx_order_id}/cancel-payment`
    }
  }

  if (module_page == 'UPSELLING') {
    return {
      api_module: 'CORE_HOTEL',
      api_url: `merchant-pos/multiple/transaction/${tx.tx_order_id_2}/cancel-transaction`
    }
  }

  if (module_page == 'CONCIERGE') {
    return {
      api_module: 'CORE_HOTEL',
      api_url: `merchant-pos/transaction/${tx.tx_order_id_2}/cancel-transaction`
    }
  }

  if (module_page == 'RESTAURANT') {
    return {
      api_module: 'CORE_RESTO',
      // The restaurant cart (non BILL) has no cancel endpoint yet — kept as-is from the legacy flow.
      api_url: submodule_page == 'BILL' ? `v2/tx-history/${tx.tx_order_id}/cancel-transaction` : ''
    }
  }

  if (module_page == 'TICKET') {
    return {
      api_module: 'CORE',
      api_url: `tx-ticket-detail/${tx.tx_order_id}/cancel-transaction`
    }
  }

  return { api_module: 'CORE_HOTEL', api_url: '' }
}

/**
 * Route to open after the payment was cancelled, per module.
 * @param {Object} context {module_page, submodule_page, tx_payment, merchant}
 * @returns {Object|String|null} argument for $router.push, or null when the module stays in place
 */
export function buildCancelRoute(context) {
  const { module_page, submodule_page, tx, merchant } = readContext(context)

  if (module_page == 'CONCIERGE_RESERVATION') {
    const reservation = tx.reservation || {}
    return `${merchant.code}/payment_bill/${reservation.pms_detail_id}?src=gp-pms&folio=${reservation.folio_number}`
  }

  if (module_page == 'CONCIERGE_TIP') {
    return { name: 'ConciergeTipResult', params: { tx_service_id: tx.tx_service_id } }
  }

  if (module_page == 'BOOKING_ENGINE') {
    if (submodule_page === 'BE_ADDITIONAL_SERVICE') return null
    return withTxSk({ name: 'BePaymentResult', params: { tx_order_id: tx.tx_order_id } }, tx)
  }

  if (module_page == 'CONCIERGE') {
    if (submodule_page == 'CUSTOM_POS') {
      return {
        name: 'PosPaymentResult',
        params: {
          merchant_code: merchant.code,
          pos_code: tx.general_data ? tx.general_data.code : '',
          tx_order_id: tx.tx_order_id_2
        }
      }
    }
    return {
      name: 'BeModuleServiceTrxResult',
      params: { merchant_code: merchant.code, trx_id: tx.tx_order_id_2 }
    }
  }

  if (module_page == 'UPSELLING') {
    return {
      name: 'GroupVoucherBookingResult',
      params: { merchant_code: merchant.code, tx_order_id: tx.tx_order_id_2 }
    }
  }

  if (module_page == 'RESTAURANT') {
    if (submodule_page == 'BILL') {
      return { name: 'MerchantHome', params: { merchant_code: merchant.code } }
    }
    return { name: 'RestaurantTrxResult', params: { merchant_code: merchant.code, trx_id: tx.tx_order_id } }
  }

  if (module_page == 'TICKET') {
    return {
      name: 'TicketPaymentForm',
      params: { merchant_code: merchant.code, tx_order_id: tx.tx_order_id }
    }
  }

  return null
}

/**
 * Whether the booking engine transaction stored in vuex must be cleared after a successful payment.
 * @param {Object} context {module_page}
 * @returns {Boolean}
 */
export function shouldClearBeTxPaymentOnSuccess(context) {
  return readContext(context).module_page == 'BOOKING_ENGINE'
}

/**
 * Whether the booking engine transaction stored in vuex must be cleared after a cancellation.
 * Additional service keeps it — that flow stays on the same page.
 * @param {Object} context {module_page, submodule_page}
 * @returns {Boolean}
 */
export function shouldClearBeTxPaymentOnCancel(context) {
  const { module_page, submodule_page } = readContext(context)
  return module_page == 'BOOKING_ENGINE' && submodule_page !== 'BE_ADDITIONAL_SERVICE'
}

/**
 * Whether the booking engine transaction stored in vuex must be cleared once it expires.
 * A leftover expired transaction makes the cart reopen straight on the payment step.
 * @param {Object} context {module_page}
 * @returns {Boolean}
 */
export function shouldClearBeTxPaymentOnExpired(context) {
  return readContext(context).module_page == 'BOOKING_ENGINE'
}



// WEBPACK FOOTER //
// ./src/utils/xenditPaymentFlow.js